/**
 * Bearer-token utilities shared across the backend, the CLI, and the MCP Worker.
 *
 * Two token types are classified here, but only `apps/backend` accepts both. The MCP
 * Worker uses `identifyTokenType` to REFUSE anything that is not a `way_` token — it has
 * no JWT lane — so do not read the `'jwt'` branch as a live MCP code path:
 * - JWT (from WorkOS AuthKit OAuth 2.1) — verified via JWKS, backend only
 * - way_ token (scoped API token) — validated via KV lookup
 */

export type TokenType = 'jwt' | 'way_token' | 'unknown';

/**
 * Extract bearer token from an HTTP request's Authorization header.
 */
export function extractBearerToken(request: Request): string | null {
  const authHeader = request.headers.get('Authorization');
  if (!authHeader?.startsWith('Bearer ')) {
    return null;
  }
  return authHeader.slice(7);
}

/**
 * Identify the token type by prefix.
 * - `way_` prefix → scoped API token (KV-backed)
 * - `eyJ` prefix → JWT (base64-encoded JSON header)
 */
export function identifyTokenType(token: string): TokenType {
  if (token.startsWith('way_')) return 'way_token';
  if (token.startsWith('eyJ')) return 'jwt';
  return 'unknown';
}
